<script setup>
import { ref, computed, watch } from 'vue';
import { Head, router, usePage } from '@inertiajs/vue3';
import { PhPlus } from '@phosphor-icons/vue';
import Layout   from '@pages/Shared/layouts/AdminLayout.vue';
import Table    from '@pages/Shared/ui/Table.vue';

const props = defineProps({
    payments:      Object,
    sessions:      Array,
    activeSession: Object,
    totals:        Object,
    categories:    Array,
    methods:       Array,
    statuses:      Array,
    initiators:    Array,
    filters:       Object,
});

// ── Filters ────────────────────────────────────────────────────────────────

const filters = ref({
    search:       null,
    session_id:   null,
    term_id:      null,
    category:     null,
    method:       null,
    status:       null,
    initiated_by: null,
    date_from:    null,
    date_to:      null,
    ...props.filters,
});

watch(() => props.filters, val => { filters.value = { ...val }; }, { deep: true });

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([, v]) => v !== null && v !== undefined && v !== '')
    );
    router.get('/admin/payments', cleaned, {
        preserveState: true, preserveScroll: true, replace: true,
    });
};

const handleSearch = (v) => { filters.value.search = v;               applyFilters(); };
const handleFilter = (f) => { filters.value = { ...filters.value, ...f }; applyFilters(); };

// ── Enum helpers ───────────────────────────────────────────────────────────

// Inertia serialises PHP enum cases as { name: 'SCHOOL_FEE', value: 'school_fee' }
const resolveEnum = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val.value) return val.value;
    return String(val);
};

const formatEnum = (val) =>
    (resolveEnum(val) ?? '').replace(/_/g, ' ');

// ── Dropdown option builders ───────────────────────────────────────────────

const categoryOptions = computed(() =>
    (props.categories ?? []).map(c => ({
        label: formatEnum(c),
        value: resolveEnum(c),
    }))
);

// Cashier cannot record Paystack payments — filter it from the method dropdown
const methodOptions = computed(() =>
    (props.methods ?? [])
        .map(m => ({ label: formatEnum(m), value: resolveEnum(m) }))
        .filter(m => m.value !== 'paystack')
);

// All methods for the filter bar (Paystack is valid to filter by — it was recorded via webhook)
const methodFilterOptions = computed(() =>
    (props.methods ?? []).map(m => ({ label: formatEnum(m), value: resolveEnum(m) }))
);

const statusOptions = computed(() =>
    (props.statuses ?? []).map(s => ({
        label: formatEnum(s),
        value: resolveEnum(s),
    }))
);

const initiatorOptions = computed(() =>
    (props.initiators ?? []).map(i => ({
        label: formatEnum(i),
        value: resolveEnum(i),
    }))
);

const sessionOptions = computed(() =>
    (props.sessions ?? []).map(s => ({ label: s.name, value: s.id }))
);

// ── Status badge colour ────────────────────────────────────────────────────

const statusColor = (val) => ({
    paid:     'bg-green-50 text-green-700',
    pending:  'bg-yellow-50 text-yellow-700',
    unpaid:   'bg-red-50 text-red-600',
    failed:   'bg-red-50 text-red-600',
    reversed: 'bg-orange-50 text-orange-600',
    waived:   'bg-gray-100 text-gray-500',
    partial:  'bg-primary-50 text-primary-700',
}[resolveEnum(val)] ?? 'bg-gray-100 text-gray-500');

// ── Initiator badge colour ─────────────────────────────────────────────────

const initiatorColor = (val) => ({
    student: 'bg-purple-50 text-purple-700',
    cashier: 'bg-amber-50  text-amber-700',
    system:  'bg-gray-100  text-gray-500',
}[resolveEnum(val)] ?? 'bg-gray-100 text-gray-500');

// ── Currency formatter ─────────────────────────────────────────────────────

const naira = (val) =>
    '₦' + Number(val ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Table columns ──────────────────────────────────────────────────────────

const columns = [
    { key: 'index',        label: '#',          type: 'index'   },
    { key: 'reference',    label: 'Reference',  type: 'slot'    },
    { key: 'student',      label: 'Student',    type: 'slot'    },
    { key: 'fee',          label: 'Fee',        type: 'slot'    },
    { key: 'amount',       label: 'Amount',     type: 'slot'    },
    { key: 'method',       label: 'Method',     type: 'slot'    },
    { key: 'initiated_by', label: 'Via',        type: 'slot'    },
    { key: 'status',       label: 'Status',     type: 'slot'    },
    { key: 'paid_at',      label: 'Date',       type: 'slot'    },
    { key: 'actions',      label: '',           type: 'actions' },
];

const page = usePage();
const can  = computed(() => page.props.can || {});
</script>

<template>
    <Layout>
        <Head title="Payments" />

        <!-- ── Header ──────────────────────────────────────────────────────── -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Payments</h2>
                <p class="text-sm text-gray-500 mt-0.5">
                    All recorded payments
                    <span v-if="activeSession" class="font-medium text-primary">
                        — {{ activeSession.name }}
                    </span>
                </p>
            </div>

            <!--
                "Record Payment" now lives on the cashier's student fee detail page,
                not here. Admins can navigate to a student's fee to record manually.
                Kept as a commented placeholder in case you want a quick-entry button later.

            <button
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhPlus :size="18" />
                Record Payment
            </button>
            -->
        </div>

        <!-- ── Summary cards ───────────────────────────────────────────────── -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm col-span-2 sm:col-span-1 lg:col-span-1">
                <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Total Collected</p>
                <p class="text-2xl font-bold text-primary">{{ naira(totals?.total_collected) }}</p>
            </div>

            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Cash</p>
                <p class="text-xl font-bold text-gray-800">{{ naira(totals?.total_cash) }}</p>
            </div>

            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Bank Transfer</p>
                <p class="text-xl font-bold text-gray-800">{{ naira(totals?.total_bank_transfer) }}</p>
            </div>

            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Online (Paystack)</p>
                <p class="text-xl font-bold text-gray-800">{{ naira(totals?.total_online) }}</p>
            </div>

            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Waived</p>
                <p class="text-xl font-bold text-gray-500">{{ naira(totals?.total_waived) }}</p>
            </div>
        </div>

        <!-- ── Manual vs Portal breakdown ─────────────────────────────────── -->
        <div class="grid grid-cols-2 gap-4 mb-6">
            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-center gap-4">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                    Cashier
                </span>
                <div>
                    <p class="text-xs text-gray-400 mb-0.5">Manual Entries</p>
                    <p class="text-lg font-bold text-gray-800">{{ naira(totals?.total_manual) }}</p>
                </div>
            </div>
            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-center gap-4">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                    Student
                </span>
                <div>
                    <p class="text-xs text-gray-400 mb-0.5">Student Portal</p>
                    <p class="text-lg font-bold text-gray-800">{{ naira(totals?.total_student_portal) }}</p>
                </div>
            </div>
        </div>

        <!-- ── Date range filter ───────────────────────────────────────────── -->
        <div class="flex items-center gap-3 mb-4 flex-wrap">
            <div class="flex items-center gap-2 text-sm text-gray-500">
                <span>From</span>
                <input
                    type="date"
                    v-model="filters.date_from"
                    @change="applyFilters"
                    class="px-3 py-1.5 text-sm border border-gray-200 rounded-lg
                           focus:ring-2 focus:ring-primary/20 outline-none transition"
                />
            </div>
            <div class="flex items-center gap-2 text-sm text-gray-500">
                <span>To</span>
                <input
                    type="date"
                    v-model="filters.date_to"
                    @change="applyFilters"
                    class="px-3 py-1.5 text-sm border border-gray-200 rounded-lg
                           focus:ring-2 focus:ring-primary/20 outline-none transition"
                />
            </div>
        </div>

        <!-- ── Payments Table ──────────────────────────────────────────────── -->
        <Table
            :data="payments"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true }"
            :filters="[
                {
                    key: 'session_id', label: 'Session',
                    options: [{ label: 'All Sessions', value: null }, ...sessionOptions],
                    value: filters.session_id,
                },
                {
                    key: 'category', label: 'Category',
                    options: [{ label: 'All Categories', value: null }, ...categoryOptions],
                    value: filters.category,
                },
                {
                    key: 'method', label: 'Method',
                    options: [{ label: 'All Methods', value: null }, ...methodFilterOptions],
                    value: filters.method,
                },
                {
                    key: 'status', label: 'Status',
                    options: [{ label: 'All Statuses', value: null }, ...statusOptions],
                    value: filters.status,
                },
                {
                    key: 'initiated_by', label: 'Via',
                    options: [{ label: 'All', value: null }, ...initiatorOptions],
                    value: filters.initiated_by,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="(id) => router.get(`/admin/payments/${id}`)"
        >
            <!-- Reference -->
            <template #reference="{ row }">
                <span class="text-xs font-mono text-gray-600 tracking-tight">
                    {{ row.reference }}
                </span>
            </template>

            <!-- Student name + admission number -->
            <template #student="{ row }">
                <div v-if="row.student_fee?.student">
                    <p class="text-sm font-medium text-gray-800 leading-tight">
                        {{ row.student_fee.student.first_name }}
                        {{ row.student_fee.student.last_name }}
                    </p>
                    <p class="text-xs text-gray-400 mt-0.5">
                        {{ row.student_fee.student.admission_number ?? '—' }}
                    </p>
                </div>
                <span v-else class="text-sm text-gray-400">—</span>
            </template>

            <!-- Fee title + category -->
            <template #fee="{ row }">
                <div v-if="row.student_fee?.fee">
                    <p class="text-sm text-gray-700 leading-tight">
                        {{ row.student_fee.fee.title }}
                    </p>
                    <p class="text-xs text-gray-400 mt-0.5 capitalize">
                        {{ formatEnum(row.category) }}
                    </p>
                </div>
                <span v-else class="text-sm text-gray-400 capitalize">
                    {{ formatEnum(row.category) || '—' }}
                </span>
            </template>

            <!-- Amount paid; strikethrough original if different -->
            <template #amount="{ row }">
                <div>
                    <p class="text-sm font-semibold text-gray-800">
                        {{ naira(row.amount_paid) }}
                    </p>
                    <p
                        v-if="Number(row.amount) !== Number(row.amount_paid)"
                        class="text-xs text-gray-400 line-through"
                    >
                        {{ naira(row.amount) }}
                    </p>
                </div>
            </template>

            <!-- Payment method -->
            <template #method="{ row }">
                <span class="text-sm text-gray-600 capitalize">
                    {{ formatEnum(row.payment_method) || '—' }}
                </span>
            </template>

            <!-- Initiated by badge (student portal vs cashier) -->
            <template #initiated_by="{ row }">
                <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                    :class="initiatorColor(row.initiated_by)"
                >
                    {{ formatEnum(row.initiated_by) || '—' }}
                </span>
            </template>

            <!-- Status badge -->
            <template #status="{ row }">
                <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                    :class="statusColor(row.status)"
                >
                    {{ resolveEnum(row.status) || '—' }}
                </span>
            </template>

            <!-- Date paid -->
            <template #paid_at="{ row }">
                <span class="text-xs text-gray-500">
                    {{ row.paid_at
                        ? new Date(row.paid_at).toLocaleDateString('en-NG', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })
                        : '—'
                    }}
                </span>
            </template>
        </Table>
    </Layout>
</template>